import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from '../assets/bharat.png';

export default function Navbar({ cart, user, onLogout, books = [] }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // ── Search state ──────────────────────────────────────────
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const inputRef = useRef(null);
    const dropRef = useRef(null);

    useEffect(() => {
        const h = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", h);
        return () => window.removeEventListener("scroll", h);
    }, []);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (
                dropRef.current && !dropRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)
            ) {
                setOpen(false);
                setHighlighted(-1);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Close on route change
    useEffect(() => {
        setOpen(false);
        setQuery("");
        setHighlighted(-1);
    }, [location.pathname]);

    // Filtered results (max 7)
    const results = useCallback(() => {
        if (!query.trim() || query.length < 2) return [];
        const q = query.toLowerCase();
        return books
            .filter(b =>
                b.title?.toLowerCase().includes(q) ||
                b.author?.toLowerCase().includes(q) ||
                b.cat?.toLowerCase().includes(q)
            )
            .slice(0, 7);
    }, [query, books])();

    const handleInput = (e) => {
        setQuery(e.target.value);
        setOpen(true);
        setHighlighted(-1);
    };

    const handleKeyDown = (e) => {
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted(h => Math.min(h + 1, results.length - 1 + (results.length > 0 ? 1 : 0)));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted(h => Math.max(h - 1, -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlighted >= 0 && highlighted < results.length) {
                goToBook(results[highlighted]);
            } else {
                goToStore();
            }
        } else if (e.key === "Escape") {
            setOpen(false);
            setHighlighted(-1);
            inputRef.current?.blur();
        }
    };

    const goToBook = (book) => {
        navigate(`/book/${book._id}`);
        window.scrollTo(0, 0);
        setOpen(false);
        setQuery("");
    };

    const goToStore = () => {
        if (!query.trim()) return;
        navigate(`/store?search=${encodeURIComponent(query.trim())}`);
        window.scrollTo(0, 0);
        setOpen(false);
        setQuery("");
    };

    const fmtINR = n => "₹" + n?.toLocaleString("en-IN");
    const stars = r => "★".repeat(Math.floor(r || 0)) + "☆".repeat(5 - Math.floor(r || 0));

    const routeMap = { home: "/", store: "/store", deals: "/deals", about: "/about", contact: "/contact", dealers: "/dealers", cart: "/cart" };
    const go = (id) => { navigate(routeMap[id] || "/"); window.scrollTo(0, 0); };

    const path = location.pathname;
    const page = path === "/" ? "home" : path.replace("/", "");

    const showDrop = open && query.length >= 2;

    const navPadding = scrolled ? "32px" : "72px";
    return (
        <>
        <style>{`
            .nav-desktop-links { display: flex !important; gap: 4px; }
            .mobile-toggle-btn { display: none !important; }
            .search-container { position: relative; display: flex; align-items: center; }

            @media (max-width: 1024px) {
                .nav-desktop-links { display: none !important; }
                .mobile-toggle-btn { display: flex !important; }
                .search-container { display: none !important; }
                .navbar-main { padding: 0 24px !important; }
            }

            .mobile-dropdown {
                position: absolute; top: 64px; left: 0; width: 100%;
                background: white; z-index: 1000; display: flex; flex-direction: column;
                padding: 16px 24px 24px;
                border-bottom: 2px solid #fae8e8;
                box-shadow: 0 10px 25px rgba(192,57,43,0.12);
                transform: translateY(-20px); opacity: 0; pointer-events: none;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }
            .mobile-dropdown.open { transform: translateY(0); opacity: 1; pointer-events: all; }
            
            .mobile-backdrop {
                position: fixed; top: 64px; left: 0; width: 100%; height: 100vh;
                background: rgba(26,5,5,0.2); backdrop-filter: blur(2px);
                z-index: 999; opacity: 0; pointer-events: none; transition: opacity 0.3s;
            }
            .mobile-backdrop.open { opacity: 1; pointer-events: all; }
        `}</style>

        <nav 
            className="navbar-main"
            style={{
                position: "sticky", top: 0, zIndex: 200, height: 64,
                background: scrolled ? "rgba(255,248,248,0.97)" : "#fff8f8",
                backdropFilter: scrolled ? "blur(16px)" : "none",
                borderBottom: `1.5px solid ${scrolled ? "#f0d0d0" : "#f5dada"}`,
                padding: "0 72px", display: "flex", alignItems: "center", justifyContent: "space-between",
                transition: "all 0.3s",
                boxShadow: scrolled ? "0 2px 20px rgba(192,57,43,0.10)" : "none",
            }}
        >
            {/* Logo */}
            <div
                style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                onClick={() => go("home")}
            >
                <div style={{
                    width: 144, height: 36, borderRadius: 10,
                    background: "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <img src={logo} alt="logo" />
                </div>
            </div>

            {/* Nav links */}
            <div className="nav-desktop-links">
                {[["home", "Home"], ["store", "Store"], ["dealers", "Dealers Network"], ["about", "About Us"], ["contact", "Contact Us"]].map(([id, label]) => (
                    <button
                        key={id}
                        onClick={() => go(id)}
                        style={{
                            padding: "8px 16px", borderRadius: 10, border: "none",
                            background: page === id ? "rgba(192,57,43,0.08)" : "transparent",
                            fontFamily: "'Nunito Sans',sans-serif",
                            fontWeight: page === id ? 700 : 500, fontSize: 14,
                            color: page === id ? "#C0392B" : "#7a4a4a",
                            cursor: "pointer", transition: "all 0.15s",
                            borderBottom: page === id ? "2px solid #C0392B" : "2px solid transparent",
                        }}
                        onMouseEnter={e => { if (page !== id) e.currentTarget.style.color = "#C0392B"; }}
                        onMouseLeave={e => { if (page !== id) e.currentTarget.style.color = "#7a4a4a"; }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Right side */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>

                {/* ── Search with Dropdown ── */}
                <div className="search-container">
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={handleInput}
                        onKeyDown={handleKeyDown}
                        onFocus={() => { if (query.length >= 2) setOpen(true); }}
                        placeholder="Search books..."
                        style={{
                            width: 200, height: 36, paddingLeft: 34, paddingRight: 32,
                            borderRadius: 50,
                            border: `1.5px solid ${showDrop ? "#C0392B" : "#f0d0d0"}`,
                            background: "#fff0f0",
                            fontFamily: "'Nunito Sans',sans-serif", fontSize: 13,
                            outline: "none", color: "#1a0505",
                            transition: "border-color 0.2s, width 0.2s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.width = "230px"}
                        onMouseLeave={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.width = "200px"; }}
                        onBlurCapture={e => {
                            // only shrink if not clicking inside dropdown
                            setTimeout(() => {
                                if (!dropRef.current?.contains(document.activeElement)) {
                                    e.target.style.width = "200px";
                                }
                            }, 150);
                        }}
                    />
                    {/* Search icon */}
                    <svg
                        style={{ position: "absolute", left: 11, color: "#c07070", cursor: query ? "pointer" : "default" }}
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                        onClick={goToStore}
                    >
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>

                    {/* Clear button */}
                    {query && (
                        <button
                            onClick={() => { setQuery(""); setOpen(false); inputRef.current?.focus(); }}
                            style={{
                                position: "absolute", right: 10,
                                background: "none", border: "none", cursor: "pointer",
                                color: "#c07070", display: "flex", alignItems: "center",
                                padding: 0, fontSize: 14, lineHeight: 1
                            }}
                        >✕</button>
                    )}

                    {/* Dropdown */}
                    {showDrop && (
                        <div
                            ref={dropRef}
                            style={{
                                position: "absolute", top: "calc(100% + 8px)", right: 0,
                                width: 360, background: "white",
                                borderRadius: 16, border: "1.5px solid #f0d0d0",
                                boxShadow: "0 12px 40px rgba(192,57,43,0.15)",
                                overflow: "hidden",
                                animation: "fadeUp 0.18s both",
                                zIndex: 300,
                            }}
                        >
                            {results.length > 0 ? (
                                <>
                                    {/* Results list */}
                                    {results.map((book, i) => (
                                        <div
                                            key={book._id}
                                            onMouseEnter={() => setHighlighted(i)}
                                            onMouseLeave={() => setHighlighted(-1)}
                                            onClick={() => goToBook(book)}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 12,
                                                padding: "10px 14px", cursor: "pointer",
                                                background: highlighted === i ? "#fff5f5" : "white",
                                                borderBottom: i < results.length - 1 ? "1px solid #fae8e8" : "none",
                                                transition: "background 0.12s"
                                            }}
                                        >
                                            {/* Tiny cover */}
                                            <div style={{
                                                width: 36, height: 50, borderRadius: 6, flexShrink: 0,
                                                background: "linear-gradient(135deg, #C0392B, #96221A)",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                fontSize: 18, boxShadow: "0 2px 8px rgba(192,57,43,0.25)"
                                            }}>
                                                {book.coverImage
                                                    ? <img src={book.coverImage} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }} />
                                                    : "📖"
                                                }
                                            </div>

                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                {/* Highlight match in title */}
                                                <div style={{
                                                    fontFamily: "'Nunito Sans',sans-serif", fontWeight: 700,
                                                    fontSize: 13, color: "#1a0505",
                                                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                                                }}>
                                                    <HighlightText text={book.title} query={query} />
                                                </div>
                                                <div style={{ fontSize: 11, color: "#c07070", marginTop: 1 }}>
                                                    <HighlightText text={book.author} query={query} />
                                                    {book.cat && <span style={{ marginLeft: 6, background: "#fff0f0", color: "#C0392B", padding: "1px 6px", borderRadius: 50, fontSize: 10, fontWeight: 700 }}>{book.cat}</span>}
                                                </div>
                                                <div style={{ fontSize: 10, color: "#D4A847", marginTop: 1 }}>{stars(book.rating)}</div>
                                            </div>

                                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                                                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, fontWeight: 800, color: "#C0392B" }}>{fmtINR(book.price)}</div>
                                               
                                            </div>
                                        </div>
                                    ))}

                                    {/* "See all results" footer */}
                                    <div
                                        onClick={goToStore}
                                        onMouseEnter={() => setHighlighted(results.length)}
                                        onMouseLeave={() => setHighlighted(-1)}
                                        style={{
                                            padding: "10px 14px", textAlign: "center",
                                            background: highlighted === results.length ? "#fff5f5" : "#fff8f8",
                                            cursor: "pointer", borderTop: "1.5px solid #fae8e8",
                                            fontFamily: "'Nunito Sans',sans-serif", fontSize: 12,
                                            fontWeight: 700, color: "#C0392B",
                                            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                                            transition: "background 0.12s"
                                        }}
                                    >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                                        </svg>
                                        See all results for "<strong>{query}</strong>"
                                    </div>
                                </>
                            ) : (
                                /* No results */
                                <div style={{ padding: "28px 14px", textAlign: "center" }}>
                                    <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                                    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, fontWeight: 700, color: "#1a0505", marginBottom: 4 }}>No books found</div>
                                    <div style={{ fontSize: 12, color: "#c07070" }}>Try a different title or author name</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Cart */}
                <button
                    onClick={() => go("cart")}
                    style={{
                        position: "relative", width: 40, height: 40, borderRadius: 12,
                        background: "#fff0f0", border: "1.5px solid #f0d0d0",
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s", color: "#8B1A1A"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#C0392B"; e.currentTarget.style.color = "white"; e.currentTarget.style.borderColor = "#C0392B"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#fff0f0"; e.currentTarget.style.color = "#8B1A1A"; e.currentTarget.style.borderColor = "#f0d0d0"; }}
                >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                    {cart.length > 0 && (
                        <span style={{
                            position: "absolute", top: -6, right: -6,
                            background: "#C0392B", color: "white",
                            fontSize: 10, fontWeight: 800, minWidth: 18, height: 18,
                            borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center",
                            animation: "badgePop 0.3s both",
                            boxShadow: "0 2px 8px rgba(192,57,43,0.4)"
                        }}>{cart.length}</span>
                    )}
                </button>

                {user ? (
                    <button
                        onClick={() => { navigate("/account"); window.scrollTo(0, 0); }}
                        style={{
                            width: 36, height: 36, borderRadius: "50%",
                            background: "linear-gradient(135deg, #C0392B, #8B1A1A)",
                            border: "1.5px solid #f0d0d0",
                            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                            transition: "all 0.2s", color: "white",
                            fontFamily: "'Playfair Display',serif", fontWeight: 800, fontSize: 14
                        }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(192,57,43,0.4)"}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                    >
                        {(user.name || "U")[0].toUpperCase()}
                    </button>
                ) : (
                    <>
                        <button
                            className="desktop-nav-only"
                            style={{
                                padding: "7px 14px", fontSize: 12, fontWeight: 600,
                                background: "transparent", border: "1.5px solid #f0d0d0",
                                color: "#8B1A1A", borderRadius: 8, cursor: "pointer",
                                fontFamily: "'Nunito Sans',sans-serif", transition: "all 0.2s"
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = "#C0392B"; e.currentTarget.style.color = "#C0392B"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = "#f0d0d0"; e.currentTarget.style.color = "#8B1A1A"; }}
                            onClick={() => { navigate("/signin"); window.scrollTo(0, 0); }}
                        >Sign In</button>
                        <button
                            className="desktop-nav-only"
                            style={{
                                padding: "8px 18px", fontSize: 12, fontWeight: 700,
                                background: "linear-gradient(135deg, #C0392B, #96221A)",
                                color: "white", border: "none", borderRadius: 8,
                                cursor: "pointer", fontFamily: "'Nunito Sans',sans-serif",
                                boxShadow: "0 3px 14px rgba(192,57,43,0.35)",
                                transition: "all 0.2s"
                            }}
                            onClick={() => { navigate("/register"); window.scrollTo(0, 0); }}
                        >Register</button>
                    </>
                )}

                {/* Mobile Hamburger Toggle */}
                <button
                    className="mobile-toggle-btn"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    style={{
                        width: 40, height: 40, borderRadius: 12,
                        background: "#fff0f0", border: "1.5px solid #f0d0d0",
                        cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                        transition: "all 0.2s", color: "#8B1A1A"
                    }}
                >
                    <div style={{ width: 18, height: 2, background: "currentColor", borderRadius: 2, transition: "0.2s", transform: mobileMenuOpen ? "rotate(45deg) translateY(8px)" : "none" }} />
                    <div style={{ width: 18, height: 2, background: "currentColor", borderRadius: 2, transition: "0.2s", opacity: mobileMenuOpen ? 0 : 1 }} />
                    <div style={{ width: 18, height: 2, background: "currentColor", borderRadius: 2, transition: "0.2s", transform: mobileMenuOpen ? "rotate(-45deg) translateY(-8px)" : "none" }} />
                </button>
            </div>

            {/* Mobile Backdrop */}
            <div className={`mobile-backdrop ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)} />

            {/* Mobile Dropdown Menu */}
            <div className={`mobile-dropdown ${mobileMenuOpen ? 'open' : ''}`}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {[["home", "Home"], ["store", "Store"], ["dealers", "Dealers Network"], ["about", "About Us"], ["contact", "Contact Us"]].map(([id, label]) => (
                        <button
                            key={id}
                            onClick={() => { go(id); setMobileMenuOpen(false); }}
                            style={{
                                padding: "12px 16px", borderRadius: 10, border: "none",
                                background: page === id ? "#fff5f5" : "transparent",
                                textAlign: "left", fontFamily: "'Nunito Sans',sans-serif",
                                fontWeight: 700, fontSize: 15,
                                color: page === id ? "#C0392B" : "#1a0505",
                                cursor: "pointer"
                            }}
                        >
                            {label}
                        </button>
                    ))}
                    
                    <div style={{ height: "1.5px", background: "#fae8e8", margin: "12px 0" }} />
                    
                    {!user ? (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <button
                                onClick={() => { navigate("/signin"); setMobileMenuOpen(false); }}
                                style={{ padding: "10px", borderRadius: 10, border: "1.5px solid #f0d0d0", background: "white", color: "#C0392B", fontWeight: 700, fontSize: 13 }}
                            >Sign In</button>
                            <button
                                onClick={() => { navigate("/register"); setMobileMenuOpen(false); }}
                                style={{ padding: "10px", borderRadius: 10, border: "none", background: "#C0392B", color: "white", fontWeight: 700, fontSize: 13 }}
                            >Register</button>
                        </div>
                    ) : (
                        <button
                            onClick={() => { navigate("/account"); setMobileMenuOpen(false); }}
                            style={{
                                display: "flex", alignItems: "center", gap: 12,
                                padding: "12px 16px", borderRadius: 10, border: "none",
                                background: "rgba(192,57,43,0.05)",
                                textAlign: "left", fontFamily: "'Nunito Sans',sans-serif",
                                fontWeight: 700, fontSize: 15,
                                color: "#C0392B",
                                cursor: "pointer"
                            }}
                        >
                            <span style={{ width: 24, height: 24, borderRadius: "50%", background: "#C0392B", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>
                                {(user.name || "U")[0].toUpperCase()}
                            </span>
                            My Account
                        </button>
                    )}
                </div>
            </div>
        </nav>
        </>
    );
}

// ── Helper: highlight matching substring ──────────────────────────────────────
function HighlightText({ text = "", query = "" }) {
    if (!query.trim()) return <>{text}</>;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return <>{text}</>;
    return (
        <>
            {text.slice(0, idx)}
            <mark style={{ background: "#ffe0e0", color: "#C0392B", borderRadius: 3, padding: "0 1px" }}>
                {text.slice(idx, idx + query.length)}
            </mark>
            {text.slice(idx + query.length)}
        </>
    );
}